import axiosInstance from "@/api/axios-instance";
import { PermissionType } from "@/helpers/role-permission";

/** Mirrors CRM_AREAS on the server; the server is the source of truth. */
export type CrmArea = string;

export type Capability = "view" | "edit";

export interface AreaAccess {
  view: boolean;
  edit: boolean;
}

export interface MyAccess {
  role: string;
  is_super: boolean;
  permissions: PermissionType[];
  areas: Record<CrmArea, AreaAccess>;
}

export interface AccessMatrix {
  /** In grid order. `group` is set on sub-areas only (the Supply Config pages),
   *  and the grid draws one heading per run of the same group. */
  areas: { key: CrmArea; label: string; group?: string }[];
  roles: {
    id: string;
    name: string;
    is_super: boolean;
    /**
     * Whether this role's Edit column can take effect at all. False when the
     * role has no `write` permission: RolesGuard requires the verb as well as
     * the area, so an Edit tick for such a role saves a row that grants nothing.
     * Returned by the server but not shown on the screen — the note that used
     * to explain it was removed by decision; the switches stay usable.
     */
    can_edit_apply: boolean;
    cells: Record<CrmArea, { view: boolean; edit: boolean; version: string }>;
  }[];
}

/** The caller's own effective access — what the permission gates run on. */
export async function getMyAccess(): Promise<MyAccess> {
  const response = await axiosInstance.get("/internal/access/me");
  return response.data;
}

export async function getAccessMatrix(): Promise<AccessMatrix> {
  const response = await axiosInstance.get("/internal/access/matrix");
  return response.data;
}

export interface SetCellResult {
  unchanged: boolean;
  version: string;
  /** The resulting pair — the server enforces "Edit implies View", so the other
   *  half of the pair may have changed too. */
  access: { view: boolean; edit: boolean };
}

export async function setAccessCell(cell: {
  area: CrmArea;
  role: string;
  capability: Capability;
  value: boolean;
  expected_version?: string;
}): Promise<SetCellResult> {
  const response = await axiosInstance.put("/internal/access/matrix/cell", cell);
  return response.data;
}
